<template>
  <UikitDialog
    :model-value="modelValue"
    position="right"
    full-height
    full-height-no-scroll
    :with-mobile-menu="!isDesktop"
    :only-top-mobile-menu="!isDesktop"
    :no-shadow="!isDesktop"
    :hide-backdrop="!isDesktop"
    close-others-on-mobile
    no-padding
    content-class="osk-concierge-dialog"
    @update:model-value="emits('update:modelValue', $event)"
  >
    <div
      v-if="formSended"
      class="osk-concierge-dialog__success"
    >
      <div class="osk-concierge-dialog__title">
        {{ $t('components.concierge.dialog.successTitle') }}
      </div>

      <div class="osk-concierge-dialog__text">
        {{ $t('components.concierge.dialog.successText') }}
      </div>

      <UikitButton
        class="osk-concierge-dialog__success-button"
        full-width
        :size="isDesktop ? 'm' : 'l'"
        @click="emits('update:modelValue', false)"
      >
        {{ $t('components.concierge.dialog.successButton') }}
      </UikitButton>
    </div>

    <UikitForm
      v-else
      class="osk-concierge-dialog__wrapper"
      @submit-validate-success="submitSuccess"
      @submit-validate-error="handleError"
    >
      <div class="osk-concierge-dialog__header">
        <div class="osk-concierge-dialog__title">
          <template v-if="isDesktop">
            {{ $t('components.concierge.dialog.formTitle') }}
          </template>

          <template v-else>
            {{ $t('components.concierge.dialog.formTitleMobile') }}
          </template>
        </div>
      </div>

      <div
        ref="$form"
        class="osk-concierge-dialog__content"
      >
        <div class="osk-concierge-dialog__subtitle">
          {{ $t('components.concierge.dialog.formSubtitle') }}

          <ConciergeMoreDetails />
        </div>

        <template v-if="isDesktop">
          <UikitFormInput
            v-model.trim="formData.lastName"
            name="lastName"
            :label="$t('components.concierge.dialog.lastName')"
            required
            capitalize
          />

          <UikitFormFieldRow>
            <UikitFormInput
              v-model.trim="formData.firstName"
              name="firstName"
              :label="$t('components.concierge.dialog.firstName')"
              required
              capitalize
            />

            <UikitFormInput
              v-if="!isInternational"
              v-model.trim="formData.secondName"
              name="secondName"
              label="Отчество"
              capitalize
            />
          </UikitFormFieldRow>
        </template>

        <template v-else>
          <UikitFormInput
            v-model.trim="formData.fio"
            name="fio"
            :label="$t('components.concierge.dialog.fio')"
            required
            capitalize
          />
        </template>

        <UikitFormPhoneInput
          v-model="formData.phoneNumber"
          name="phoneNumber"
          :label="$t('components.concierge.dialog.phoneNumber')"
          :only-countries="onlyCountries"
          required
          rules="phone2"
        />

        <UikitFormInput
          v-model.trim="formData.comment"
          name="wishes"
          :label="$t('components.concierge.dialog.wishes')"
          :placeholder="$t('components.concierge.dialog.wishesPlaceholder')"
          textarea
          rules="maxLength:10000"
        />

        <UikitFile
          class="osk-concierge-dialog__images"
          :max-images="6"
          :upload-image="uploadImage"
          @remove-img="removeImg"
        />
      </div>

      <div class="osk-concierge-dialog__footer">
        <UikitButton
          full-width
          :loading="loadingSendForm"
          :size="isDesktop ? 'm' : 'l'"
        >
          {{ $t('components.concierge.dialog.send') }}
        </UikitButton>
      </div>

      <notifications
        group="concierge"
        position="bottom right"
        :duration="-1"
        width="auto"
      >
        <template #body="{ item, close }">
          <div class="osk-concierge-dialog__notification">
            <p class="osk-concierge-dialog__notification-title">
              {{ item.title }}
            </p>

            <div
              class="osk-concierge-dialog__notification-text"
              v-html="item.text"
            />

            <UikitButton
              full-width
              type="button"
              @click="close"
            >
              {{ $t('components.concierge.dialog.ok') }}
            </UikitButton>
          </div>
        </template>
      </notifications>
    </UikitForm>
  </UikitDialog>
</template>

<script setup lang="ts">
import { useNotification } from '@kyvg/vue3-notification';

import { useApi } from '~/restAPI';
import { Concierge } from '~/restAPI/Concierge';
import { Address } from '~/restAPI/Address';
import { SubmitApplicationDTO } from '~/restAPI/data-contracts';

import { scrollToFirstErrorInContainer } from '~/utils/scroll';

import { AnalyticsEvents } from '~/types/analytics';

const props = defineProps<{
  modelValue: boolean
  page?: string
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: boolean): void,
  (e: 'success'): void,
}>();

const {
  isDesktop,
} = useUiBreakpoints();
const { $addEvent } = useNuxtApp();

const $form = ref<HTMLElement>();

const formData = ref({
  fio: '',
  lastName: '',
  firstName: '',
  secondName: '',
  phoneNumber: '',
  comment: '',
});

const loadingSendForm = ref(false);
const formSended = ref(false);

const images = ref<string[] | null>();

const { isInternational } = useInternational();
const { t } = useI18n();
const { notify } = useNotification();
const { createApiInstance } = useApi();
const conciergeApi = createApiInstance(Concierge);
const addressApi = createApiInstance(Address);

const { data: countries, execute: getCountries } = useLazyAsyncData(() => addressApi.getCountriesGet(
  { context: 'PHONE' },
  { format: 'json' },
).then((res) => res?.data?.data), { immediate: false });

const onlyCountries = computed(() => countries.value?.map((c) => c.isoCodeAlpha2!)
  .filter((c) => !!c));

function removeImg(index: number) {
  images.value?.splice(index, 1);
}

async function uploadImage(file: File) {
  try {
    const data = await conciergeApi.uploadItemImageUsingPost({ image: file })
      .then((res) => res.data?.data);

    if (images.value) {
      images.value.push(...data);

      if (images.value.length > 6) {
        images.value = images.value.slice(-6);
      }
    } else {
      images.value = data;
    }

    return { data };
  } catch (error) {
    return { error };
  }
}

function handleError() {
  scrollToFirstErrorInContainer($form.value);
}

async function submitSuccess() {
  loadingSendForm.value = true;

  const {
    lastName, firstName, secondName, phoneNumber, comment, fio,
  } = formData.value;

  const options: SubmitApplicationDTO = {
    clientName: isDesktop.value ? (`${lastName} ${firstName} ${secondName}`).trim() : fio.trim(),
    phoneNumber,
    comment,
    clientChannel: 'WEB',
  };

  if (images.value) {
    options.imagesReferences = images.value;
  }

  const { error } = await useAsyncData(() => conciergeApi.submitApplicationUsingPost(options, {
    format: 'json',
  }));

  loadingSendForm.value = false;

  if (error.value) {
    let errorText = t('components.concierge.dialog.errorTitle');

    if (error.value.response) {
      const respJson = await error.value.response.json();

      if (respJson && respJson.message) {
        errorText = respJson.message;
      }
    }

    notify({
      group: 'concierge',
      title: t('components.concierge.dialog.errorText'),
      text: errorText,
    });
  } else {
    formSended.value = true;

    $addEvent(AnalyticsEvents.ConciergeFormSubmit);
  }
}

watch(() => props.modelValue, async (value) => {
  if (value) {
    await getCountries();
  } else {
    setTimeout(() => {
      images.value = undefined;
      formSended.value = false;
      formData.value = {
        fio: '',
        lastName: '',
        firstName: '',
        secondName: '',
        phoneNumber: '',
        comment: '',
      };
    }, 500);
  }
});
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-concierge-dialog {
  @include font-body-all;

  padding-top: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;

  @include media-query(lg-and-up) {
    width: 504px !important;
    padding-bottom: 0 !important;
  }

  .osk-dialog__close {
    top: 22px;
    right: 16px !important;

    @include media-query(lg-and-up) {
      top: 44px;
      right: 50px !important;
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;

    @include media-query(lg-and-up) {
      padding-top: 100px;
    }

    .vue-notification-wrapper {
      padding: 16px !important;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    padding: 22px 16px;

    @include media-query(lg-and-up) {
      padding: 0 52px;
    }
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    line-height: normal;

    @include media-query(lg-and-up) {
      margin-bottom: $indent-mmedium;
    }
  }

  &__subtitle {
    background: $grey-4;
    color: #000;
    font-size: 12px;
    font-weight: 500;
    line-height: normal;
    margin-bottom: $indent-large;
    padding: $indent-medium;

    @include media-query(lg-and-up) {
      background: #fff;
      color: $grey;
      margin-bottom: $indent-mlarge;
      font-size: 14px;
      font-weight: 500;
      padding: 0;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    height: 100%;
    padding: 0 $indent-medium;
    position: relative;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @include media-query(lg-and-up) {
      padding: 0 52px 16px;
    }
  }

  &__images {
    margin-top: $indent-compact;
  }

  &__footer {
    padding: $indent-medium;

    @include media-query(lg-and-up) {
      padding: $indent-medium 52px 52px;
    }
  }

  &__notification {
    padding: 20px 22px;
    background: #fff;
    box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.12);

    @include media-query(md-and-up) {
      width: 400px;
      margin-right: 36px;
    }
  }

  &__notification-title {
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
    margin-bottom: 12px;
  }

  &__notification-text {
    margin-bottom: 16px;
  }

  &__success {
    height: 100%;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @include media-query(md-and-up) {
      padding: 0 52px;
    }

    .osk-concierge-dialog__title {
      margin-top: auto;

      @include media-query(md-and-up) {
        margin-top: 0;
      }
    }
  }

  &__success-button {
    margin-top: auto;
    margin-bottom: 16px;

    @include media-query(md-and-up) {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &__text {
    margin-bottom: 52px;
    margin-top: 12px;
    line-height: 20px;
  }

  &__grey {
    color: $grey;
  }

  &__files {
    margin-top: 32px;

    @include media-query(lg-and-up) {
      margin-top: 0;
    }

    .osk-collapse-item__title {
      @include media-query(lg-and-up) {
        display: none;
      }
    }
  }
}
</style>
